/**
 * DataFreshness Component
 *
 * Shows when data was last updated and provides manual refresh option.
 */

import { useState, useEffect } from 'react';

interface DataFreshnessProps {
  /** Timestamp when data was last fetched */
  dataUpdatedAt: number;
  /** Optional: callback to trigger refresh */
  onRefresh?: () => void;
  /** Is currently refreshing? */
  isRefreshing?: boolean;
}

export function DataFreshness({
  dataUpdatedAt,
  onRefresh,
  isRefreshing = false,
}: DataFreshnessProps) {
  const [, setTick] = useState(0);

  // Update every 10 seconds to keep "X seconds ago" current
  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 10000);
    return () => clearInterval(interval);
  }, []);

  if (!dataUpdatedAt) return null;

  const secondsAgo = Math.floor((Date.now() - dataUpdatedAt) / 1000);

  const formatTime = (seconds: number): string => {
    if (seconds < 5) return 'Just now';
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    return new Date(dataUpdatedAt).toLocaleTimeString();
  };

  return (
    <div className="flex items-center gap-2 text-xs text-gray-500">
      {/* Status indicator dot */}
      <span
        className={`w-2 h-2 rounded-full ${
          isRefreshing ? 'bg-yellow-400 animate-pulse' : 'bg-green-400'
        }`}
        title={isRefreshing ? 'Refreshing...' : 'Data is fresh'}
      />

      <span>{isRefreshing ? 'Updating...' : `Updated ${formatTime(secondsAgo)}`}</span>

      {onRefresh && (
        <button
          onClick={() => onRefresh()}
          disabled={isRefreshing}
          className="text-primary-600 hover:text-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          title="Refresh now"
        >
          <svg
            className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
        </button>
      )}
    </div>
  );
}
